import type { PuzzleBotDifficulty, PuzzleTeamId } from "../simulation";

export type PuzzleRoomPreset = "duel" | "team-ai" | "team" | "tournament";

export interface PuzzleRoomDraft {
  preset: PuzzleRoomPreset;
  name: string;
  botDifficulty: PuzzleBotDifficulty;
  hostKey: string;
}

export interface CreatedPuzzleRoom {
  tournamentId: string;
  matchIds: string[];
  name: string;
  preset: PuzzleRoomPreset;
  joinUrl: string;
  expiresAt: string;
  humanCapacity: 2 | 4;
}

export interface PuzzleRoomPresetSpec {
  mode: "duel" | "team";
  humanCount: 2 | 4;
  title: string;
  note: string;
}

export interface PuzzleRoomPreviewParticipant {
  name: string;
  team: PuzzleTeamId;
}

export interface PuzzleRoomPreview {
  id: string;
  name: string;
  preset: PuzzleRoomPreset;
  mode: "duel" | "team";
  humanCapacity: 2 | 4;
  teamCapacity: 1 | 2;
  joinedHumans: number;
  full: boolean;
  started: boolean;
  allowTeamChoice: boolean;
  expiresAt: string;
  participants: PuzzleRoomPreviewParticipant[];
}

export interface JoinedPuzzleRoom {
  matchId: string;
  playerId: string;
  ticket: string;
  gameKind: "puzzle";
  roomName: string;
  nickname: string;
  team: PuzzleTeamId;
}

export const PUZZLE_ROOM_PRESETS: Readonly<Record<PuzzleRoomPreset, PuzzleRoomPresetSpec>> = {
  duel: {
    mode: "duel",
    humanCount: 2,
    title: "1:1 온라인",
    note: "두 사람이 링크에서 닉네임과 진영 선택",
  },
  "team-ai": {
    mode: "team",
    humanCount: 2,
    title: "2:2 + AI",
    note: "사람 2명 입장 후 빈 자리는 AI로 확정",
  },
  team: {
    mode: "team",
    humanCount: 4,
    title: "2:2 온라인",
    note: "사람 4명이 로비에서 A/B팀 선택",
  },
  tournament: {
    mode: "duel",
    humanCount: 4,
    title: "1:1 토너먼트",
    note: "4명이 닉네임 입력 후 대진 자동 배정",
  },
};

interface CreatePuzzleRoomOptions {
  origin?: string;
  localDevelopment?: boolean;
  fetcher?: typeof fetch;
}

interface RoomRequestOptions {
  origin?: string;
  fetcher?: typeof fetch;
}

interface PuzzleRoomBootstrapBody {
  game: "puzzle";
  preset: PuzzleRoomPreset;
  botDifficulty: PuzzleBotDifficulty;
  name: string;
}

export function buildPuzzleRoomBootstrapBody(draft: PuzzleRoomDraft): PuzzleRoomBootstrapBody {
  const name = draft.name.trim().slice(0, 80);
  if (!name) throw new Error("방 이름을 입력해 주세요.");
  if (!PUZZLE_ROOM_PRESETS[draft.preset]) throw new Error("경기 형식을 확인해 주세요.");
  return { game: "puzzle", preset: draft.preset, botDifficulty: draft.botDifficulty, name };
}

export async function createPuzzleRoom(
  draft: PuzzleRoomDraft,
  options: CreatePuzzleRoomOptions = {},
): Promise<CreatedPuzzleRoom> {
  const body = buildPuzzleRoomBootstrapBody(draft);
  const origin = normalizedOrigin(options.origin ?? browserOrigin());
  const localDevelopment = options.localDevelopment ?? isLocalDevelopmentHostname(new URL(origin).hostname);
  const hostKey = draft.hostKey.trim();
  if (!localDevelopment && !hostKey) throw new Error("개인 호스트 키를 입력해 주세요.");
  if (/\r|\n/u.test(hostKey) || hostKey.length > 512) throw new Error("호스트 키 형식을 확인해 주세요.");

  const headers = new Headers({ Accept: "application/json", "Content-Type": "application/json" });
  const endpoint = localDevelopment && !hostKey ? "/api/dev/rooms" : "/api/admin/rooms";
  if (hostKey) headers.set("x-foam-admin-secret", hostKey);
  const response = await timedFetch(options.fetcher ?? fetch, `${origin}${endpoint}`, {
    method: "POST",
    credentials: "same-origin",
    headers,
    body: JSON.stringify(body),
  }, "방 생성 응답이 늦어 중단했습니다. 다시 시도해 주세요.");
  const payload = await readJsonRecord(response, "create");
  if (!response.ok) throw roomRequestError(response.status, responseErrorCode(payload), "create");
  return parseCreatedPuzzleRoom(payload, draft, origin);
}

export function parseCreatedPuzzleRoom(
  payload: Record<string, unknown>,
  draft: PuzzleRoomDraft,
  origin: string,
): CreatedPuzzleRoom {
  const tournament = record(payload.tournament);
  const singleMatch = record(payload.match);
  const room = record(payload.room);
  const matchIds = [
    stringValue(singleMatch.id),
    ...array(payload.matches).map((entry) => stringValue(record(entry).id)),
  ].filter((value, index, values) => Boolean(value) && values.indexOf(value) === index);
  const tournamentId = stringValue(tournament.id);
  const joinPath = stringValue(room.joinPath);
  const base = normalizedOrigin(origin);
  const joinUrl = new URL(joinPath, `${base}/`);
  const humanCapacity = room.humanCapacity === 4 ? 4 : room.humanCapacity === 2 ? 2 : 0;
  if (!tournamentId || matchIds.length === 0 || !joinPath || joinUrl.origin !== base || humanCapacity === 0) {
    throw new Error("서버가 완전한 방 정보를 반환하지 않았습니다.");
  }
  return {
    tournamentId,
    matchIds,
    name: stringValue(tournament.name) || draft.name.trim(),
    preset: room.preset === "team-ai" || room.preset === "team" || room.preset === "tournament"
      ? room.preset
      : "duel",
    joinUrl: joinUrl.toString(),
    expiresAt: stringValue(room.expiresAt),
    humanCapacity,
  };
}

export async function previewPuzzleRoom(
  token: string,
  options: RoomRequestOptions = {},
): Promise<PuzzleRoomPreview> {
  const origin = normalizedOrigin(options.origin ?? browserOrigin());
  const response = await timedFetch(options.fetcher ?? fetch, `${origin}/api/rooms/preview`, {
    method: "POST",
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  }, "방 정보를 불러오지 못했습니다. 다시 시도해 주세요.");
  const payload = await readJsonRecord(response, "preview");
  if (!response.ok) throw roomRequestError(response.status, responseErrorCode(payload), "preview");
  return parsePuzzleRoomPreview(record(payload.room));
}

export async function joinPuzzleRoom(
  token: string,
  nickname: string,
  team: PuzzleTeamId | null,
  options: RoomRequestOptions = {},
): Promise<JoinedPuzzleRoom> {
  const normalizedNickname = nickname.trim().replace(/\s+/gu, " ");
  if (!normalizedNickname || normalizedNickname.length > 24) {
    throw new Error("닉네임은 1~24자로 입력해 주세요.");
  }
  const origin = normalizedOrigin(options.origin ?? browserOrigin());
  const response = await timedFetch(options.fetcher ?? fetch, `${origin}/api/rooms/join`, {
    method: "POST",
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ token, nickname: normalizedNickname, ...(team ? { team } : {}) }),
  }, "입장 응답이 늦어 중단했습니다. 다시 시도해 주세요.");
  const payload = await readJsonRecord(response, "join");
  if (!response.ok) throw roomRequestError(response.status, responseErrorCode(payload), "join");
  const player = record(payload.player);
  const matchId = stringValue(payload.matchId) || stringValue(player.matchId);
  const playerId = stringValue(payload.playerId) || stringValue(player.id);
  const ticket = stringValue(payload.ticket) || stringValue(payload.wsTicket);
  const resolvedTeam = player.team === "orange" ? "orange" : player.team === "blue" ? "blue" : null;
  if (!matchId || !playerId || !ticket || !resolvedTeam) {
    throw new Error("서버가 유효한 참가 좌석을 반환하지 않았습니다.");
  }
  return {
    matchId,
    playerId,
    ticket,
    gameKind: "puzzle",
    roomName: stringValue(record(payload.room).name),
    nickname: stringValue(player.name) || normalizedNickname,
    team: resolvedTeam,
  };
}

export function parsePuzzleRoomPreview(room: Record<string, unknown>): PuzzleRoomPreview {
  const preset: PuzzleRoomPreset = room.preset === "team-ai" || room.preset === "team" || room.preset === "tournament"
    ? room.preset
    : "duel";
  const humanCapacity = room.humanCapacity === 4 ? 4 : room.humanCapacity === 2 ? 2 : 0;
  const teamCapacity = room.teamCapacity === 2 ? 2 : room.teamCapacity === 1 ? 1 : 0;
  const participants = array(room.participants).map((value) => {
    const participant = record(value);
    return {
      name: stringValue(participant.name),
      team: participant.team === "orange" ? "orange" as const : "blue" as const,
    };
  }).filter((participant) => participant.name);
  if (!stringValue(room.id) || !stringValue(room.name) || !humanCapacity || !teamCapacity) {
    throw new Error("방 정보 형식을 읽을 수 없습니다.");
  }
  return {
    id: stringValue(room.id),
    name: stringValue(room.name),
    preset,
    mode: room.mode === "team" ? "team" : "duel",
    humanCapacity,
    teamCapacity,
    joinedHumans: typeof room.joinedHumans === "number" ? room.joinedHumans : participants.length,
    full: room.full === true,
    started: room.started === true,
    allowTeamChoice: room.allowTeamChoice !== false,
    expiresAt: stringValue(room.expiresAt),
    participants,
  };
}

export function isLocalDevelopmentHostname(hostname: string): boolean {
  return hostname === "127.0.0.1"
    || hostname === "localhost"
    || hostname.startsWith("192.168.")
    || hostname.startsWith("10.")
    || /^172\.(?:1[6-9]|2\d|3[01])\./u.test(hostname);
}

function browserOrigin(): string {
  if (typeof location === "undefined" || !location.origin) {
    throw new Error("방 서버 주소를 확인할 수 없습니다.");
  }
  return location.origin;
}

function normalizedOrigin(value: string): string {
  const parsed = new URL(value);
  if (parsed.protocol !== "https:" && !(parsed.protocol === "http:" && isLocalDevelopmentHostname(parsed.hostname))) {
    throw new Error("멀티플레이 방은 HTTPS 또는 로컬 개발 주소에서만 사용할 수 있습니다.");
  }
  return parsed.origin;
}

async function timedFetch(
  fetcher: typeof fetch,
  input: string,
  init: RequestInit,
  timeoutMessage: string,
): Promise<Response> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15_000);
  try {
    return await fetcher(input, { ...init, signal: controller.signal });
  } catch (error) {
    if (controller.signal.aborted) throw new Error(timeoutMessage);
    throw new Error(error instanceof Error ? `서버 연결 실패: ${error.message}` : "서버에 연결할 수 없습니다.");
  } finally {
    clearTimeout(timeout);
  }
}

type RoomAction = "create" | "preview" | "join";

function responseErrorCode(payload: Record<string, unknown>): string {
  return stringValue(record(payload.error).code) || stringValue(payload.code);
}

async function readJsonRecord(response: Response, action: RoomAction): Promise<Record<string, unknown>> {
  try {
    return record(await response.json());
  } catch {
    if (!response.ok) throw roomRequestError(response.status, "invalid_response", action);
    throw new Error("서버가 읽을 수 없는 응답을 보냈습니다.");
  }
}

function roomRequestError(status: number, code: string, action: RoomAction): Error {
  if (action === "create" && (status === 401 || status === 403)) {
    return new Error("호스트 키가 올바르지 않거나 방 생성 권한이 없습니다.");
  }
  if (code === "room_full") return new Error("방 정원이 모두 찼습니다.");
  if (code === "team_full") return new Error("선택한 팀이 가득 찼습니다. 다른 팀을 선택해 주세요.");
  if (code === "room_started") return new Error("이미 시작한 경기입니다.");
  if (code === "room_unavailable" && action !== "create") return new Error("방 주소가 만료됐거나 더 이상 사용할 수 없습니다.");
  if (status === 404) return new Error("이 주소에서 멀티플레이 서버에 연결할 수 없습니다. 멀티플레이 시작 주소를 확인해 주세요.");
  if (code === "invalid_room") return new Error("올바른 방 주소가 아닙니다. 방장이 공유한 링크를 다시 열어 주세요.");
  if (code === "team_required") return new Error("A팀 또는 B팀을 선택해 주세요.");
  if (status === 429) return new Error("요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.");
  if (status >= 500) return new Error("방 서버에 문제가 생겼습니다. 잠시 후 다시 시도해 주세요.");
  return new Error(`${action === "create" ? "방을 만들지" : "방에 입장하지"} 못했습니다 (${status}${code ? ` · ${code}` : ""}).`);
}

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function array(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value : "";
}
